"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import Image from "next/image"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet"
import { Menu } from "lucide-react"
import type { PortfolioData } from "@/types/portfolio"

interface HeaderProps {
  data: PortfolioData
}

const baseNavLinks = [
  { href: "#funcionalidades", label: "Plataforma" },
  { href: "#resultados", label: "Resultados" },
  { href: "#faq", label: "FAQ" },
] as const

export function Header({ data }: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const navLinks = [
    ...baseNavLinks,
    { href: data.contact.whatsapp, label: "Demo", external: true },
  ]

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20)
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 max-lg:bg-white max-lg:border-b max-lg:border-gray-100 ${
        isScrolled
          ? "bg-white border-b border-gray-100 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link href="/" className="flex items-center shrink-0">
            <Image
              src="/LOGO.jpg"
              alt="AsistDental"
              width={160}
              height={40}
              className="h-8 w-auto sm:h-9 lg:h-10"
              priority
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                {...("external" in link && link.external
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {})}
                className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:block">
            <Button
              asChild
              size="sm"
              className="text-white"
              style={{ backgroundColor: "#F97316" }}
            >
              <a href={data.profile.ctaUrl} target="_blank" rel="noopener noreferrer">
                {data.profile.ctaText}
              </a>
            </Button>
          </div>

          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" aria-label="Abrir menú">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:w-80 p-6">
              <div className="flex flex-col h-full">
                <SheetTitle className="mb-8">
                  <Image
                    src="/LOGO.jpg"
                    alt="AsistDental"
                    width={140}
                    height={36}
                    className="h-8 w-auto"
                  />
                </SheetTitle>
                <nav className="flex flex-col gap-1">
                  {navLinks.map((link) => (
                    <Link
                      key={link.href}
                      href={link.href}
                      {...("external" in link && link.external
                        ? { target: "_blank", rel: "noopener noreferrer" }
                        : {})}
                      onClick={() => setIsOpen(false)}
                      className="px-4 py-3 text-base font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
                    >
                      {link.label}
                    </Link>
                  ))}
                </nav>
                <div className="mt-auto pt-8">
                  <Button
                    asChild
                    className="w-full text-white"
                    size="lg"
                    style={{ backgroundColor: "#F97316" }}
                  >
                    <a
                      href={data.profile.ctaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsOpen(false)}
                    >
                      {data.profile.ctaText}
                    </a>
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  )
}
